import { isObject } from '@vueuse/core'
import { captureException } from '@sentry/vue'
import { useToast } from '@/composables/useToast'

class ServerError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ServerError'
  }
}

const isErrorResponse = (response: unknown): response is errorResponse => {
  return isObject(response) && 'errors' in response
}

type errorResponse = {
  status: number
  message: string
  errors: Record<string, string[]>
}

/**
 * Shared function to handle errors from an API. Like Rendering error messages.
 */
export async function handleErrorResponse(request: RequestInit, response: Response, overrideToast?: ReturnType<typeof useToast>) {
  const toast = overrideToast ?? useToast()

  if (response.status === 500) { // Handle server errors, cannot be handled by the client
    toast.error( 'Het verzoek kon niet worden verwerkt. Het development team, is op de hoogte gebracht.')

    return Promise.reject(new ServerError('Server error'))
  }

  const responseBody = await response.json()
  if (!isErrorResponse(responseBody)) {
    toast.error('Er is een fout opgetreden. Probeer het later opnieuw.')

    return Promise.reject(new ServerError('Invalid error response format'))
  }

  const errorCodes = responseBody.errors

  const errorCount = Object.keys(errorCodes).length
  if (errorCount === 0) {
    toast.error('Er is een fout opgetreden. Probeer het later opnieuw.')
    captureException(new ServerError('No error messages found'), {
      extra: {
        request: request,
        response: responseBody,
        status: response.status,
        url: response.url,
      },
    })

    return Promise.reject(new ServerError('No error messages found'))
  }

  // Handle client errors, like validation errors. Multiple errors can be rendered, so we loop through them.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  for (const [ code, errors ] of Object.entries(errorCodes)) {
    errors.forEach(errorStr => toast.error(errorStr))
  }

  return Promise.reject(new ServerError('Server error'))
}